import React, { useEffect, useState } from 'react';
import { apiService } from '../../../../services/api';

// Spec §6.2 step 1 — select an existing push connection (PowerBI / Tableau Cloud).
// If user has none, an inline "Create connection" form appears.
const StepConnection = ({ formData, onSetFormData }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newConn, setNewConn] = useState({ kind: 'powerbi', name: '', endpoint: '' });
  const [error, setError] = useState(null);

  const reload = async () => {
    try {
      setLoading(true);
      const resp = await apiService.listPushConnections();
      const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
      setConnections(items);
    } catch (err) {
      setError(err.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSelect = (conn) => {
    onSetFormData({ ...formData, connectionId: conn.id, connectionKind: conn.kind });
  };

  const handleCreate = async () => {
    if (!newConn.name) return;
    try {
      setError(null);
      const resp = await apiService.createPushConnection(newConn);
      setCreating(false);
      await reload();
      if (resp?.id) handleSelect(resp);
    } catch (err) {
      setError(err.message || 'Failed to create connection');
    }
  };

  return (
    <div className="push-step-content">
      <h3 className="push-step-title">Step 1 — Push connection</h3>
      <p className="push-step-help">Pick where this automation should push results. PowerBI and Tableau Cloud are supported.</p>
      {error && <div className="push-step-error">{error}</div>}
      {loading ? (
        <p>Loading connections...</p>
      ) : (
        <>
          {connections.length === 0 && !creating && (
            <p className="push-step-empty">No connections yet. Create one below.</p>
          )}
          <ul className="push-connection-list" data-testid="push-connection-list">
            {connections.map((c) => (
              <li
                key={c.id}
                className={`push-connection-item ${formData.connectionId === c.id ? 'selected' : ''}`}
                onClick={() => handleSelect(c)}
              >
                <span className="push-connection-name">{c.name}</span>
                <span className="push-connection-kind">{c.kind}</span>
              </li>
            ))}
          </ul>
          {creating ? (
            <div className="push-new-connection">
              <label className="push-label">Kind</label>
              <select
                className="resui-select"
                value={newConn.kind}
                onChange={(e) => setNewConn({ ...newConn, kind: e.target.value })}
              >
                <option value="powerbi">PowerBI</option>
                <option value="tableau_cloud">Tableau Cloud</option>
              </select>
              <label className="push-label">Name</label>
              <input
                className="resui-input"
                value={newConn.name}
                onChange={(e) => setNewConn({ ...newConn, name: e.target.value })}
                placeholder="e.g. Finance PowerBI workspace"
              />
              <label className="push-label">Endpoint</label>
              <input
                className="resui-input"
                value={newConn.endpoint}
                onChange={(e) => setNewConn({ ...newConn, endpoint: e.target.value })}
                placeholder="https://..."
              />
              <div className="push-new-connection-actions">
                <button className="push-secondary-button" onClick={() => setCreating(false)}>Cancel</button>
                <button className="push-primary-button" onClick={handleCreate} disabled={!newConn.name}>Save connection</button>
              </div>
            </div>
          ) : (
            <button className="push-link-button" onClick={() => setCreating(true)}>+ New connection</button>
          )}
        </>
      )}
    </div>
  );
};

export default StepConnection;
